import type { ReactNode } from "react";

import {
  DEFAULT_THEME_ID,
  THEME_PALETTES,
  type ThemeColors,
  type ThemePalette,
} from "@cmmd-forge/contracts/theme";

import { cn } from "~/lib/utils";

import { ThemeWireframe, type WireframeColors } from "./ThemeWireframe";

/**
 * Appearance picker: colour scheme first, then palettes.
 *
 * Both rows preview as miniatures of the app rather than as swatches. A swatch
 * answers "what colours is this"; a miniature answers "what will this feel like
 * to sit in", which is the question being asked. It also makes the system
 * option honest — it can show light and dark split down the middle, which no
 * single swatch can.
 */

export type AppearanceMode = "system" | "light" | "dark";

const MODE_LABELS: Readonly<Record<AppearanceMode, string>> = {
  dark: "Dark",
  light: "Light",
  system: "System",
};

/** The shipped tokens, for previewing the default. Mirrors `index.css`. */
const FORGE_WIREFRAME: Readonly<Record<"light" | "dark", WireframeColors>> = {
  dark: {
    accent: "oklch(1 0 0 / 8%)",
    border: "oklch(1 0 0 / 6%)",
    canvas: "oklch(0.145 0 0)",
    card: "oklch(0.169 0 0)",
    primary: "oklch(0.62 0.2 47)",
    sidebar: "oklch(0 0 0)",
  },
  light: {
    accent: "oklch(0 0 0 / 7%)",
    border: "oklch(0.922 0 0)",
    canvas: "oklch(0.992 0 0)",
    card: "oklch(1 0 0)",
    primary: "oklch(0.52 0.19 48)",
    sidebar: "oklch(0.97 0 0)",
  },
};

/**
 * A palette may omit any role, so each slot falls back to the shipped preview
 * rather than to a hard-coded colour — otherwise a partial palette would
 * preview surfaces it does not actually change.
 */
function wireframeFor(colors: ThemeColors, appearance: "light" | "dark"): WireframeColors {
  const base = FORGE_WIREFRAME[appearance];
  return {
    accent: colors.accent ?? base.accent,
    border: colors.border ?? base.border,
    canvas: colors.background ?? base.canvas,
    card: colors.card ?? base.card,
    primary: colors.primary ?? base.primary,
    sidebar: colors.sidebar ?? base.sidebar,
  };
}

function Tile(props: {
  readonly label: string;
  readonly selected: boolean;
  readonly ariaLabel: string;
  readonly onSelect: () => void;
  readonly panes: readonly {
    readonly colors: WireframeColors;
    readonly clip?: "left" | "right" | undefined;
  }[];
  readonly caption?: string | undefined;
}): ReactNode {
  return (
    <button
      aria-checked={props.selected}
      aria-label={props.ariaLabel}
      tabIndex={props.selected ? 0 : -1}
      className={cn(
        "flex cursor-pointer flex-col items-stretch gap-1.5 rounded-xl border p-2 text-left outline-none transition-colors",
        "focus-visible:ring-2 focus-visible:ring-ring",
        // Selection is carried by a primary edge plus the tinted body. The
        // tint alone measured 1.08:1 against the resting tile (forge#3358),
        // far under the 3:1 non-text contrast floor, and the transparent
        // border removed the one signal that could have carried it. The
        // edge is the palette's own primary, so a selected tile announces
        // itself in whatever palette is being previewed.
        props.selected
          ? "border-primary bg-accent/30"
          : "border-border/70 bg-card/60 hover:bg-accent",
      )}
      onClick={props.onSelect}
      role="radio"
      type="button"
    >
      <ThemeWireframe panes={props.panes} />
      <span className="min-w-0 px-0.5">
        <span
          className={cn(
            "block truncate font-medium text-xs",
            props.selected ? "text-foreground" : "text-muted-foreground",
          )}
        >
          {props.label}
        </span>
        {props.caption === undefined ? null : (
          <span className="block truncate text-[11px] text-secondary-label">{props.caption}</span>
        )}
      </span>
    </button>
  );
}

/**
 * ARIA radiogroup keyboard model: the checked radio is the group's only tab
 * stop, and the arrow keys move focus and selection together, wrapping at the
 * ends. forge#3358 found both pickers exposing every tile as a tab stop with
 * no arrow handling, which strands keyboard users.
 */
function handleRadiogroupKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
  const delta = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
  if (delta === undefined) return;
  const radios = [...event.currentTarget.querySelectorAll<HTMLElement>('[role="radio"]')];
  const current = radios.indexOf(document.activeElement as HTMLElement);
  if (current === -1) return;
  event.preventDefault();
  const next = radios[(current + delta + radios.length) % radios.length];
  if (next === undefined) return;
  next.focus();
  next.click();
}

export function AppearanceModePicker(props: {
  readonly mode: AppearanceMode;
  readonly themeId: string;
  readonly onModeChange: (mode: AppearanceMode) => void;
}): ReactNode {
  const palette = THEME_PALETTES.find((candidate) => candidate.id === props.themeId);

  const paneFor = (appearance: "light" | "dark"): WireframeColors =>
    palette === undefined
      ? FORGE_WIREFRAME[appearance]
      : wireframeFor(appearance === "dark" ? palette.dark : palette.light, appearance);

  return (
    <div
      aria-label="Colour scheme"
      // auto-fit, not a fixed column count — same rule as the palette grid
      // below. A tile claims at least 16rem, which holds exactly three columns
      // at the settings content width and reflows to one column at phone
      // widths. A fixed `grid-cols-3` would keep dividing whatever width it is
      // given, which is how it ended up crushed into a settings-row control
      // column.
      className="grid w-full gap-3 [grid-template-columns:repeat(auto-fit,minmax(min(100%,16rem),1fr))]"
      role="radiogroup"
      onKeyDown={handleRadiogroupKeyDown}
    >
      {(["system", "light", "dark"] as const).map((mode) => (
        <Tile
          ariaLabel={
            mode === "system" ? "Follow the system appearance" : `Use ${MODE_LABELS[mode]} mode`
          }
          key={mode}
          label={MODE_LABELS[mode]}
          onSelect={() => props.onModeChange(mode)}
          panes={
            mode === "system"
              ? [
                  { clip: "left", colors: paneFor("light") },
                  { clip: "right", colors: paneFor("dark") },
                ]
              : [{ colors: paneFor(mode) }]
          }
          selected={props.mode === mode}
        />
      ))}
    </div>
  );
}

export function ThemePicker(props: {
  readonly themeId: string;
  readonly appearance: "light" | "dark";
  /** Installed theme files, rendered after the built-ins. */
  readonly customThemes?: ReadonlyArray<ThemePalette>;
  readonly onThemeIdChange: (themeId: string) => void;
}): ReactNode {
  const options: readonly (ThemePalette | null)[] = [
    null,
    ...THEME_PALETTES,
    ...(props.customThemes ?? []),
  ];

  return (
    <div
      aria-label="Theme palette"
      // auto-fit, not a fixed column count — this is the reference's own grid.
      // A tile claims at least 16rem and the row reflows to whatever fits, so
      // the picker can never be squeezed narrower than a legible tile. A fixed
      // `grid-cols-3` would keep dividing whatever width it is given, which is
      // how it ended up crushed into a settings-row control column.
      className="grid w-full gap-2 [grid-template-columns:repeat(auto-fit,minmax(min(100%,16rem),1fr))]"
      role="radiogroup"
      onKeyDown={handleRadiogroupKeyDown}
    >
      {options.map((palette) => {
        const id = palette?.id ?? DEFAULT_THEME_ID;
        const name = palette?.name ?? "Forge";
        const colors =
          palette === null
            ? FORGE_WIREFRAME[props.appearance]
            : wireframeFor(
                props.appearance === "dark" ? palette.dark : palette.light,
                props.appearance,
              );
        return (
          <Tile
            ariaLabel={`Use the ${name} theme`}
            caption={palette?.description}
            key={id}
            label={name}
            onSelect={() => props.onThemeIdChange(id)}
            panes={[{ colors }]}
            selected={props.themeId === id}
          />
        );
      })}
    </div>
  );
}
